import type { ReactNode } from 'react';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdAnyScope, findMoveByIdGlobal } from '@/data/moves';
import { findSpellById } from '@/data/spells';
import type { Character, MoveRef, SpellRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import type { ClassId } from '@/schemas/classId';
import type { Move } from '@/schemas/move';
import { scoreToModifier, type StatId } from '@/schemas/stats';
import { getEffectiveBaseHp } from '@/services/characterCreation';
import { renderInlineMarkdown } from '@/utils/inlineMarkdown';
import type { LevelUpGrantedChoiceDraft } from './types';

// page-specific (Fase 2 PR 10): step «Conferma» del flusso di salita di
// livello. Pixel-parity dal pattern mockup `.lvup-review` (styles.css
// 2018-2042): righe grid 170px label + 1fr value con dotted border-bottom,
// label Cinzel uppercase + value font-serif.
//
// D-Q15 (SSOT): 1 tabella su schermata, non card per delta.
//
// **PR 10 cambio API**: il button "Conferma e applica L{N}" è ora
// delegato a `<StepNav isLast lastLabel>` esterno renderizzato da
// `LevelUpFlowPage` (decisione E1). Le prop `onConfirm` / `onBack`
// rimosse. Il componente espone solo `error` per il render dell'errore
// di apply.

interface LevelUpStepConfirmProps {
  character: Character;
  classDef: ClassDefinition;
  /** Statistica aumentata; assente nel flow Apprendista/Instancabile (PG L10+ skippa stat). */
  statIncreased?: StatId;
  /** Mossa scelta; assente nel flow Addio (PG si ritira, no mossa). */
  moveAdded?: MoveRef;
  /** Definizione della mossa scelta; coerente con `moveAdded`. */
  moveDef?: Move;
  secondaryArchetypeId?: string;
  /** D2/FI-33: nuovi spell che verranno aggiunti via grantsSpells. */
  spellsAdded?: readonly SpellRef[];
  /** D5/FI-31: sotto-scelta concessa dalla mossa, se applicabile. */
  grantedChoice?: LevelUpGrantedChoiceDraft;
  /** **D10 / FI-11**: scelta cruciale post-10° (Addio / Apprendista / Instancabile). */
  pastTen?: 'addio' | 'apprendista' | 'instancabile';
  error: string | null;
}

export function LevelUpStepConfirm(props: LevelUpStepConfirmProps) {
  const delta = computeDelta(props);
  const isAddio = props.pastTen === 'addio';

  return (
    <section>
      <SectionHeader
        title={
          isAddio ? `Conferma il ritiro del personaggio` : `Conferma il livello ${delta.nextLevel}`
        }
        sub={
          isAddio
            ? 'Il personaggio si ritira leggendario. Confermando, il ritiro viene applicato e salvato.'
            : 'Rivedi le scelte. Confermando vengono applicate al personaggio e salvate.'
        }
      />
      <ReviewTable props={props} delta={delta} />
      {props.error ? (
        <p className="border-accent-dwr bg-accent-dwr/10 text-accent-dwr mt-3 border px-3 py-2 font-serif text-[0.92rem]">
          {props.error}
        </p>
      ) : null}
    </section>
  );
}

function ReviewTable({ props, delta }: { props: LevelUpStepConfirmProps; delta: ConfirmDelta }) {
  return (
    <div className="border-rule bg-paper-bright mt-4 border px-[1.4rem] py-[1.2rem]">
      <ReviewRow label="Livello">
        <span className="font-display">
          {props.character.level} → <strong className="font-bold">{delta.nextLevel}</strong>
        </span>
      </ReviewRow>
      {props.pastTen ? <PastTenRow pastTen={props.pastTen} /> : null}
      <StatRow statIncreased={props.statIncreased} delta={delta} />
      <HpDeltaRow props={props} delta={delta} />
      <MoveRow moveDef={props.moveDef} />
      <SecondaryArchetypeRow
        classDef={props.classDef}
        secondaryArchetypeId={props.secondaryArchetypeId}
      />
      <SpellsAddedRow spellsAdded={props.spellsAdded} />
      {props.moveDef ? (
        <GrantedChoiceRow
          classDef={props.classDef}
          moveDef={props.moveDef}
          grantedChoice={props.grantedChoice}
        />
      ) : null}
    </div>
  );
}

function StatRow({
  statIncreased,
  delta,
}: {
  statIncreased: StatId | undefined;
  delta: ConfirmDelta;
}) {
  if (statIncreased === undefined) return null;
  return (
    <ReviewRow label={`Caratteristica · ${statIncreased}`}>
      <span className="font-display">
        {delta.prevStat} {formatMod(scoreToModifier(delta.prevStat))} →{' '}
        <strong className="font-bold">
          {delta.nextStat} {formatMod(scoreToModifier(delta.nextStat))}
        </strong>
      </span>
    </ReviewRow>
  );
}

function HpDeltaRow({ props, delta }: { props: LevelUpStepConfirmProps; delta: ConfirmDelta }) {
  if (delta.hpDelta === 0) return null;
  const hasOverride =
    props.secondaryArchetypeId !== undefined && delta.nextBaseHp !== delta.prevBaseHp;
  return (
    <ReviewRow label="PF max">
      <span className="font-display">
        {props.character.hp.current}/{delta.prevHpMax} →{' '}
        <strong className="font-bold">
          {props.character.hp.current + delta.hpDelta}/{delta.nextHpMax}
        </strong>
        {delta.cosDelta > 0 ? <ReviewSub text="Costituzione +1" /> : null}
        {hasOverride ? <ReviewSub text="Override PF base dal secondo archetipo" /> : null}
      </span>
    </ReviewRow>
  );
}

function MoveRow({ moveDef }: { moveDef: Move | undefined }) {
  if (!moveDef) return null;
  const replaceText =
    moveDef.replaces.length > 0
      ? `Sostituisce: ${moveDef.replaces.map((id) => findMoveByIdGlobal(id)?.name ?? id).join(', ')}`
      : undefined;
  return (
    <ReviewRow label="Mossa scelta">
      <span className="font-serif">
        <strong className="font-semibold">{moveDef.name}</strong>
        {replaceText ? <ReviewSub text={replaceText} /> : null}
        <div className="text-ink-soft mt-[0.4rem] text-[0.92rem] italic leading-relaxed">
          {renderInlineMarkdown(moveDef.description)}
        </div>
      </span>
    </ReviewRow>
  );
}

function SecondaryArchetypeRow({
  classDef,
  secondaryArchetypeId,
}: {
  classDef: ClassDefinition;
  secondaryArchetypeId: string | undefined;
}) {
  if (secondaryArchetypeId === undefined) return null;
  return (
    <ReviewRow label="Secondo archetipo">
      <span className="font-serif">
        <strong className="font-semibold">
          {resolveArchetypeName(classDef, secondaryArchetypeId)}
        </strong>
      </span>
    </ReviewRow>
  );
}

const PAST_TEN_LABELS: Record<'addio' | 'apprendista' | 'instancabile', string> = {
  addio: "L'Addio (ritiro)",
  apprendista: "L'Apprendista",
  instancabile: "L'Instancabile",
};

function PastTenRow({ pastTen }: { pastTen: 'addio' | 'apprendista' | 'instancabile' }) {
  return (
    <ReviewRow label="Scelta cruciale post-10°">
      <span className="font-serif">
        <strong className="font-semibold">{PAST_TEN_LABELS[pastTen]}</strong>
      </span>
    </ReviewRow>
  );
}

interface ReviewRowProps {
  label: string;
  children: ReactNode;
}

function ReviewRow({ label, children }: ReviewRowProps) {
  return (
    <div className="border-rule grid grid-cols-[170px_1fr] items-baseline gap-[0.6rem] border-b border-dotted py-[0.6rem] last:border-b-0 max-[600px]:grid-cols-1 max-[600px]:gap-[0.25rem]">
      <span className="font-display text-ink-soft text-[0.78rem] tracking-[0.08em] uppercase">
        {label}
      </span>
      <span className="text-ink min-w-0">{children}</span>
    </div>
  );
}

function ReviewSub({ text }: { text: string }) {
  return <div className="text-ink-soft mt-[0.3rem] font-serif text-[0.85rem] italic">{text}</div>;
}

function GrantedChoiceRow({
  classDef,
  moveDef,
  grantedChoice,
}: {
  classDef: ClassDefinition;
  moveDef: Move;
  grantedChoice: LevelUpGrantedChoiceDraft | undefined;
}) {
  if (!grantedChoice || !moveDef.grantsMoveChoice) return null;
  const label = lookupGrantedLabel(
    classDef,
    moveDef.grantsMoveChoice,
    grantedChoice.selectedOptionId,
  );
  return (
    <ReviewRow label={`Sotto-scelta da «${moveDef.name}»`}>
      <span className="font-serif">
        <strong className="font-semibold">{label}</strong>
      </span>
    </ReviewRow>
  );
}

function SpellsAddedRow({ spellsAdded }: { spellsAdded: readonly SpellRef[] | undefined }) {
  if (!spellsAdded || spellsAdded.length === 0) return null;
  const names = spellsAdded.map((ref) => findSpellById(ref.id)?.name ?? ref.id);
  return (
    <ReviewRow label="Nuovi incantesimi">
      <span className="font-serif">
        <strong className="font-semibold">{names.length}</strong>
        <ReviewSub text={names.join(', ')} />
      </span>
    </ReviewRow>
  );
}

interface ConfirmDelta {
  nextLevel: number;
  prevStat: number;
  nextStat: number;
  cosDelta: number;
  prevBaseHp: number;
  nextBaseHp: number;
  prevHpMax: number;
  nextHpMax: number;
  hpDelta: number;
}

function computeDelta(props: LevelUpStepConfirmProps): ConfirmDelta {
  const { character, classDef, statIncreased, secondaryArchetypeId } = props;
  const nextLevel = character.level + 1;
  // Quando statIncreased è undefined (caso Addio / post-10°), gli HP/stat
  // delta sono 0 (nessun aumento). Fallback su 'forza' come stat-default
  // ma il display in JSX è già gated da `statIncreased !== undefined`.
  const prevStat = statIncreased !== undefined ? character.stats[statIncreased] : 0;
  const nextStat = statIncreased !== undefined ? prevStat + 1 : 0;
  const cosDelta = statIncreased === 'costituzione' ? 1 : 0;
  const prevBaseHp = getEffectiveBaseHp(
    classDef,
    character.archetypeId,
    character.secondaryArchetypeId,
  );
  const nextBaseHp = getEffectiveBaseHp(
    classDef,
    character.archetypeId,
    secondaryArchetypeId ?? character.secondaryArchetypeId,
  );
  const prevHpMax = character.hp.max;
  const nextHpMax = nextBaseHp + character.stats.costituzione + cosDelta;
  return {
    nextLevel,
    prevStat,
    nextStat,
    cosDelta,
    prevBaseHp,
    nextBaseHp,
    prevHpMax,
    nextHpMax,
    hpDelta: nextHpMax - prevHpMax,
  };
}

function resolveArchetypeName(classDef: ClassDefinition, archetypeId: string): string {
  return classDef.archetypes.find((a) => a.id === archetypeId)?.name ?? archetypeId;
}

function lookupGrantedLabel(
  classDef: ClassDefinition,
  grant: NonNullable<Move['grantsMoveChoice']>,
  optionId: string,
): string {
  if (grant.kind === 'from-group') return lookupFromGroupLabel(classDef, optionId);
  if (grant.kind === 'from-choice-move') {
    return lookupFromChoiceMoveLabel(classDef, grant.moveId, optionId);
  }
  return lookupFromChoiceMappingLabel(optionId);
}

function lookupFromGroupLabel(classDef: ClassDefinition, optionId: string): string {
  return findMoveByIdAnyScope(classDef.id, optionId)?.name ?? optionId;
}

function lookupFromChoiceMoveLabel(
  classDef: ClassDefinition,
  sourceMoveId: string,
  optionId: string,
): string {
  const sourceMove = findMoveByIdAnyScope(classDef.id, sourceMoveId);
  return sourceMove?.choices?.find((c) => c.id === optionId)?.label ?? optionId;
}

function lookupFromChoiceMappingLabel(optionId: string): string {
  const move = findMoveByIdGlobal(optionId);
  if (!move) return optionId;
  if (move.classId === undefined) return move.name;
  const classLabel = dwrClassesById[move.classId as ClassId]?.name;
  return classLabel !== undefined ? `${move.name} (${classLabel})` : move.name;
}

function formatMod(mod: number): string {
  return mod >= 0 ? `(+${mod})` : `(${mod})`;
}
